/**
 * React Query persister for IndexedDB storage.
 * Persists the query cache to IndexedDB for offline access.
 */
import type { Persister, PersistedClient } from '@tanstack/react-query-persist-client';
import type { QueryClient } from '@tanstack/react-query';
import { offlineStorage } from './OfflineStorageService';

const CACHE_KEY = 'panluma-react-query-cache';

/**
 * Query-key substrings whose queries are never persisted — real-time or
 * frequently changing data that would be misleading if restored stale.
 * Single source of truth for both `persistClient` and `shouldPersistQuery`.
 */
const NO_PERSIST_KEY_PATTERNS = ['notifications', 'websocket', 'typing', 'presence', 'health'];

/** Whether a query key matches one of the no-persist patterns. */
function matchesNoPersistPattern(queryKey: readonly unknown[]): boolean {
  const keyString = JSON.stringify(queryKey).toLowerCase();
  return NO_PERSIST_KEY_PATTERNS.some((pattern) => keyString.includes(pattern));
}

/**
 * Whether a value survives the structured-clone algorithm — the same algorithm
 * IndexedDB's `put` uses internally.
 *
 * A single query whose cached data holds a non-cloneable value (a function, DOM
 * node, etc. — classically a raw axios response carrying its `config.transformRequest`
 * function because a queryFn forgot to unwrap `.data`) makes the WHOLE `put`
 * throw `DataCloneError`. The persist then fails silently and the entire
 * persisted cache freezes at its last good snapshot — which is how a stale,
 * empty board list survived reloads on desktop web. Dropping just the offending
 * query keeps one poison pill from blocking persistence for every other query.
 *
 * Falls back to `true` (persist as-is, original behaviour) on engines without
 * `structuredClone` rather than dropping everything.
 */
function isStructuredCloneable(value: unknown): boolean {
  if (typeof structuredClone !== 'function') {
    return true;
  }
  try {
    structuredClone(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Custom persister that uses IndexedDB via OfflineStorageService.
 */
export const indexedDBPersister: Persister = {
  persistClient: async (client: PersistedClient): Promise<void> => {
    try {
      // Filter out queries that shouldn't be persisted
      const filteredClient: PersistedClient = {
        ...client,
        clientState: {
          ...client.clientState,
          queries: client.clientState.queries.filter((query) => {
            // Drop queries that have no cached data to restore. React Query
            // keeps prior `data` even when a refetch errors (offline cold-start
            // is exactly this case: success → offline → refetch fails →
            // status='error' but `data` still holds the cached payload). We
            // intentionally do NOT gate on `status === 'error'` here — doing
            // so wiped the IndexedDB cache every time the device went offline.
            if (query.state.data === undefined) {
              return false;
            }
            // Honour an explicit per-query opt-out: meta: { persist: false }.
            // Used for session-scoped data (e.g. announcement delivery) that
            // must be re-fetched fresh on every app load, never restored stale.
            if (query.meta?.persist === false) {
              return false;
            }
            // Check query key for patterns we don't want to persist
            if (matchesNoPersistPattern(query.queryKey)) {
              return false;
            }
            // Drop any query whose data can't survive structured clone so a
            // single non-cloneable query can't make the whole IndexedDB write
            // throw DataCloneError and freeze the entire persisted cache.
            if (!isStructuredCloneable(query)) {
              console.warn(
                '[offline] Skipping non-cloneable query during cache persist:',
                JSON.stringify(query.queryKey),
              );
              return false;
            }
            return true;
          }),
          mutations: [], // Don't persist pending mutations in Phase 1
        },
      };
      await offlineStorage.setQueryCache(CACHE_KEY, filteredClient);
    } catch (error) {
      console.error('Failed to persist query cache:', error);
    }
  },

  restoreClient: async (): Promise<PersistedClient | undefined> => {
    try {
      const client = await offlineStorage.getQueryCache(CACHE_KEY) as PersistedClient | null;
      return client || undefined;
    } catch (error) {
      console.error('Failed to restore query cache:', error);
      return undefined;
    }
  },

  removeClient: async (): Promise<void> => {
    try {
      await offlineStorage.deleteQueryCache(CACHE_KEY);
    } catch (error) {
      console.error('Failed to remove query cache:', error);
    }
  },
};

/**
 * Purge the entire query cache — both the in-memory `QueryClient` and the
 * persisted IndexedDB snapshot — so no data survives an auth transition.
 *
 * React Query's persisted cache is shared across every user on a browser
 * profile (one `PersistQueryClientProvider` at the app root). It is NOT cleared
 * on login/logout, so on a shared device a newly-logged-in user would briefly
 * render the previous user's cached lists before their own fetches replace them
 * (portal capstone finding M3 — customer B seeing customer A's shipments).
 *
 * Call this from every auth-transition path (login success AND logout).
 * `clear()` empties memory synchronously; `removeClient()` deletes the
 * IndexedDB copy so a subsequent reload can't restore the stale snapshot.
 */
export async function clearPersistedQueryCache(queryClient: QueryClient): Promise<void> {
  queryClient.clear();
  await indexedDBPersister.removeClient();
}

/**
 * Whether a query should be persisted, by query key alone.
 *
 * `persistClient` already applies this check (plus a `meta.persist: false`
 * opt-out) to every query. This helper is exported for callers that want to
 * reason about a key directly, e.g. in tests or prefetch logic.
 */
export function shouldPersistQuery(queryKey: readonly unknown[]): boolean {
  return !matchesNoPersistPattern(queryKey);
}

/**
 * Whether a query should be included in the dehydrated snapshot.
 *
 * React Query's default (`status === 'success'`) drops every query that has
 * ever errored — including queries that succeeded once online, were refetched
 * after the device went offline, errored on the refetch, but still hold the
 * previously-cached `data` in memory. That default plus our `staleTime: 0`
 * setting means going offline silently strips the cache of everything the user
 * viewed, defeating the whole point of read-only offline mode.
 *
 * Keep any query that holds defined `data` regardless of status — that's the
 * same predicate the IndexedDB persister filter below uses. The error itself
 * is still redacted by React Query's `shouldRedactErrors` default, so we
 * don't leak stale error objects on hydrate.
 */
function shouldDehydrateQueryForOffline(query: {
  state: { data: unknown };
}): boolean {
  return query.state.data !== undefined;
}

/**
 * Get persistence options for React Query.
 */
export function getPersistOptions() {
  return {
    persister: indexedDBPersister,
    maxAge: 24 * 60 * 60 * 1000, // 24 hours
    buster: import.meta.env.VITE_VERSION || 'dev', // Invalidate on deployment
    dehydrateOptions: {
      shouldDehydrateQuery: shouldDehydrateQueryForOffline,
    },
  };
}
